'use client';

/**
 * SelectionReadMenu — right-click context menu over chat text selection.
 *
 * Behavior:
 *   - Right-click anywhere inside the chat scroll area.
 *   - If there's a non-empty text selection, intercept the native context menu.
 *   - Show a small custom menu at cursor: "Read" + voice picker (2 voices preloaded).
 *   - Click "Read with <voice>" → POST /api/voice/speak.
 *   - Click outside / Escape → dismiss.
 *
 * Voices: af_heart (default English warm) + af_bella (alt English softer).
 *   To change voices, edit the VOICES constant below.
 */

import { useEffect, useRef, useState } from 'react';

type Voice = { id: string; label: string };

const VOICES: Voice[] = [
  { id: 'af_heart', label: 'Heart (default)' },
  { id: 'af_bella', label: 'Bella (alt)' },
];

interface Pos {
  x: number;
  y: number;
}

interface Props {
  containerRef: React.RefObject<HTMLElement | null>;
}

export function SelectionReadMenu({ containerRef }: Props) {
  const [pos, setPos] = useState<Pos | null>(null);
  const [selectedText, setSelectedText] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null); // voice id while speaking
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Attach context-menu handler to container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handler = (e: MouseEvent) => {
      const sel = window.getSelection();
      // Match server-side normalization: trim + collapse whitespace runs
      const text = (sel?.toString() ?? '').trim().replace(/\s+/g, ' ');
      if (!text) return;
      // Selection must be inside our container
      const range = sel?.getRangeAt(0);
      if (!range || !el.contains(range.commonAncestorContainer)) return;

      e.preventDefault();
      setSelectedText(text);
      setError(null); // Always clear stale errors on new menu open
      setPos({ x: e.clientX, y: e.clientY });
    };

    el.addEventListener('contextmenu', handler);
    return () => el.removeEventListener('contextmenu', handler);
  }, [containerRef]);

  // Dismiss on click-outside or Escape
  useEffect(() => {
    if (!pos) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setPos(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pos]);

  const speak = async (voiceId: string) => {
    if (!selectedText) return;
    setBusy(voiceId);
    setError(null);
    try {
      const res = await fetch('/api/voice/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: selectedText, voice: voiceId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error ?? 'speak failed');
      } else {
        setPos(null); // dismiss on success
      }
    } catch (e: any) {
      setError(e?.message ?? 'network error');
    } finally {
      setBusy(null);
    }
  };

  if (!pos) return null;

  // Clamp to viewport
  const x = Math.min(pos.x, window.innerWidth - 220);
  const y = Math.min(pos.y, window.innerHeight - 120);

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] min-w-[200px] rounded-md border border-[rgb(var(--border))] bg-[#0d1117]/85 shadow-lg text-sm"
      style={{ left: x, top: y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="px-3 py-1.5 text-[11px] text-[rgb(var(--muted-foreground))] border-b border-[rgb(var(--border))]/60">
        Read selection ({selectedText.length} chars{selectedText.length > 3500 ? ` — ${Math.ceil(selectedText.length / 3500)} chunks` : ''})
      </div>
      {VOICES.map((v) => (
        <button
          key={v.id}
          onClick={() => speak(v.id)}
          disabled={busy !== null}
          className="w-full text-left px-3 py-2 hover:bg-[rgb(var(--accent))]/10 disabled:opacity-50 flex items-center justify-between"
        >
          <span>Read with {v.label}</span>
          {busy === v.id && <span className="text-xs opacity-60">…</span>}
        </button>
      ))}
      {error && (
        <div className="px-3 py-1.5 text-[11px] text-red-400 border-t border-[rgb(var(--border))]/60">
          {error}
        </div>
      )}
    </div>
  );
}
